import React, { memo, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import { texte, PANELS } from '../components/startseite/texte';
import HandyAngebote from '../components/startseite/HandyAngebote';
import { starteTitelSzene } from '../components/startseite/titelszene';
import { starteAbschnitte } from '../components/startseite/abschnitte';
import StartAbschnitte from '../components/startseite/StartAbschnitte';
import '../components/startseite/startseite.css';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';

// Neue Startseite (Entwurf aus ~/Projects/davidmedia-header-test, 04.10.2026).
// Kopfbereich = Header-Version 11 „Titelbild mit Wechsel“ (header/h11.html), darunter die
// Abschnitte der Demo. Die Leiste oben bleibt die runde FloatingHeader-Pille, alles läuft
// über die volle Breite.
//
// Szene und Abschnitte arbeiten direkt am DOM (Klassen umschalten, Zähler, Ladebalken). Deshalb
// ist der Inhalt ein memo-Baustein, der nach dem ersten Zeichnen nicht neu rendert —
// sonst würde React die gesetzten Klassen wieder überschreiben.

// Sprung zu einem Abschnitt der Seite (statt #-Links, die den Router-Pfad verbiegen)
function springe(e, id) {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
}

// Handy (unter 861 px): oben ein wechselndes Titelbild (HandyAngebote), darunter nur Überschrift und
// Knopf (startseite.css, .handykopf). Nur dort eingebaut, damit der PC die Bilder gar nicht erst lädt.
const HANDY = '(max-width: 860px)';
function useHandy() {
  const [handy, setHandy] = useState(() => typeof window !== 'undefined' && window.matchMedia(HANDY).matches);
  useEffect(() => {
    const mq = window.matchMedia(HANDY);
    const neu = () => setHandy(mq.matches);
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);
  return handy;
}

const Inhalt = memo(function Inhalt({ en }) {
  const T = texte(en);
  const handy = useHandy();
  return (
    <>
      <section className="held handykopf" id="held">
        <div className="text">
          <div>
            {handy && <HandyAngebote en={en} />}
            <span className="kicker">{T.hero.kicker}</span>
            <h1 className="h1">{T.hero.h1a} <span className="grau">{T.hero.h1b}</span></h1>
            <div className="ts-punkte">
              {T.hero.punkte.map(([zahl, wort, klein]) => (
                <div className="ts-punkt" key={wort}><b><span>{zahl}</span> {wort}</b><small>{klein}</small></div>
              ))}
            </div>
            <div className="ctas">
              <a className="btn primaer" href="#installer" onClick={e => springe(e, 'installer')}>{T.hero.cta1}</a>
              <a className="btn glas" href="#all-in-one" onClick={e => springe(e, 'all-in-one')}>{T.hero.cta2}</a>
            </div>
          </div>
        </div>
      </section>

      <StartAbschnitte en={en} seite="start" />
    </>
  );
});

const Home = () => {
  const location = useLocation();
  const en = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const wurzel = useRef(null);

  useEffect(() => {
    const el = wurzel.current;
    const T = texte(en);
    const stoppAbschnitte = starteAbschnitte({ wurzel: el, T: T.szene, panels: PANELS, preise: T.preise });
    // Die Werkzeuge, durch die das Panel im Kopfbereich wechselt (Reihenfolge wie h11)
    const stoppKopf = starteTitelSzene(el.querySelector('#held'), { produkte: ['shake-flow', 'bounce-flow', 'carousel-flow', 'background-flow', 'grid-flow'] });
    return () => { stoppKopf(); stoppAbschnitte(); };
  }, [en]);


  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title={en ? 'DAVIDMEDIA | Extensions for After Effects' : 'DAVIDMEDIA | Extensions für After Effects'}
        description={en
          ? 'Twelve After Effects tools that save you a step each: buy them one by one or get them all in the All-in-One. Plus free presets and an installer for Mac and Windows.'
          : 'Zwölf Werkzeuge für After Effects, die dir je einen Arbeitsschritt abnehmen: einzeln kaufen oder alle im All-in-One. Dazu kostenlose Presets und ein Installer für Mac und Windows.'}
        url={en ? 'https://www.davidmedia.de/en' : 'https://www.davidmedia.de/'}
      />
      <FloatingHeader />
      <div className="startseite" ref={wurzel}>
        <Inhalt en={en} />
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </motion.div>
  );
};

export default Home;
